import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { RegistrationPageImageResponse } from 'services/openapi';
import { ImageCardSkeletonLoader } from './ImageCardSkeletonLoader';
import { CustomDeletePopup } from 'app/components/CustomDeletePopup';
import AdminCustomImageCropper from 'admin/components/AdminCustomImageCropper';
import { AdminCustomPopUp } from 'app/components/AdminCustomPopUp';
import AdminCustomButton from 'app/components/AdminCustomButton';
import { CommonLoader } from 'app/components/CommonLoader';
import { Errortext } from 'app/components/AdminTextField';
import useRegistrationPageImageApi from 'admin/hooks/useRegistrationPageImageApi ';

// Import default images
import LoginBgImg from './assets/images/loginBg.jpg';
import TeamRegImg from './assets/images/team-reg-new.png';
import CoachRegImg from './assets/images/coach-reg-new.jpg';
import RiderNewImg from './assets/images/riderNew.png';
import EwdReturningImg from './assets/images/ewdReturning.jpg';
import AlumniReturningImg from './assets/images/AlumniRetunring.jpg';
import AssociateReturningImg from './assets/images/associateReturning.jpg';

// Default images mapping for when imageUrl is null
const DEFAULT_IMAGES: Record<string, string> = {
  homebackground: LoginBgImg,
  registerteam: TeamRegImg,
  registercoach: CoachRegImg,
  registerrider: RiderNewImg,
  registertryitteam: TeamRegImg,
  registertryitcoach: CoachRegImg,
  registertryitrider: RiderNewImg,
  registertryitewd: EwdReturningImg,
  registertryitalumni: AlumniReturningImg,
  registerewd: EwdReturningImg,
  alumni: AlumniReturningImg,
  rider: RiderNewImg,
  ewd: EwdReturningImg,
  associate: AssociateReturningImg,
  RiderDashboardBgImage:
    'https://images.unsplash.com/photo-1466495227171-d05d7e3ac2b3?q=80&w=1229&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
};

const DashboardContainer = styled.div`
  padding: 15px 0;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 1280px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const ImageCard = styled.div`
  border: 1px solid #dceaff;
  border-radius: 8px;
  background-color: #edf4fd99;
  overflow: hidden;
  transition: all 0.2s ease-in-out;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    box-shadow: 0 4px 12px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06);
    transform: translateY(-2px);
  }
`;

const ImagePreview = styled.div<{ imageUrl?: string }>`
  width: 100%;
  height: 180px;
  background-color: #f3f4f6;
  background-image: ${props =>
    props.imageUrl ? `url('${props.imageUrl.replace(/\\/g, '/')}')` : 'none'};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const NoPreviewPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #9ca3af;
  pointer-events: none;
  user-select: none;

  svg {
    opacity: 0.5;
  }

  span {
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.02em;
  }
`;

const CardContent = styled.div`
  padding: 18px 16px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 12px 0;
  letter-spacing: 0.05em;
  min-height: 44px;
  display: flex;
  align-items: center;
  line-height: 1.3;
  word-break: break-word;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: auto;
`;

const Button = styled.button<{ variant?: 'primary' | 'danger' }>`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid
    ${props => (props.variant === 'danger' ? '#ef4444' : '#3b82f6')};
  background-color: ${props =>
    props.variant === 'danger' ? '#fee2e2' : '#eff6ff'};
  color: ${props => (props.variant === 'danger' ? '#dc2626' : '#1d4ed8')};
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: ${props =>
      props.variant === 'danger' ? '#fecaca' : '#dbeafe'};
    border-color: ${props =>
      props.variant === 'danger' ? '#dc2626' : '#1d4ed8'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const NoDataContainer = styled.div`
  grid-column: 1 / -1;
  padding: 40px 20px;
  text-align: center;
  color: #6b7280;
  font-size: 16px;
`;

const FlexWrapper = styled.div`
  padding: 10px 20px 15px 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  max-height: 800px;
  overflow-y: auto;
`;

const ImageUploadArea = styled.div<{ isDragActive?: boolean }>`
  border: 2px dashed ${props => (props.isDragActive ? '#3b82f6' : '#cbd5e0')};
  border-radius: 8px;
  padding: 30px 20px;
  text-align: center;
  background-color: ${props => (props.isDragActive ? '#eff6ff' : '#f9fafb')};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 150px;

  &:hover {
    border-color: #3b82f6;
    background-color: #eff6ff;
  }
`;

const UploadIcon = styled.div`
  font-size: 32px;
  margin-bottom: 10px;
`;

const UploadText = styled.p`
  font-size: 14px;
  color: #1f2937;
  margin: 0;
  font-weight: 500;
`;

const UploadSubText = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 5px 0 0 0;
`;

const ImagePreviewContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 200px;
  margin: 0 auto;
  border-radius: 8px;
  overflow: hidden;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  background-color: rgba(239, 68, 68, 0.9);
  color: white;
  border: none;
  border-radius: 4px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(220, 38, 38, 1);
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: end;
  width: 100%;
  gap: 10px;
  align-items: baseline;
  margin-top: 10px;
  .button-style {
    button {
      max-width: unset !important;
      width: auto !important;
    }
  }
`;
const FullImagePreviewModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  overflow: auto;
`;

const ModalImage = styled.img`
  width: 100%;
  height: auto;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 4px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.9);
  }
`;

const ImageInfoPanel = styled.div`
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ImageInfo = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #6b7280;
  gap: 10px;
  .label {
    font-weight: 600;
    color: #1f2937;
  }
`;

const PreviewButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgb(255 255 255 / 72%);
  color: #000;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  opacity: 1;
  transition: opacity 0.2s;
  font-size: 13px;
  font-weight: 600;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;

  &:hover {
    background-color: rgba(59, 130, 246, 1);
    color: #fff;
  }
`;

interface ImageCardComponentProps {
  image: RegistrationPageImageResponse;
  onUpload: (type: number, file: Blob) => Promise<string | void>;
  onDelete: (type: number) => Promise<void>;
  isLoading?: boolean;
}

const getDefaultImageUrl = (
  typeName: string | null | undefined,
): string | undefined => {
  if (!typeName) return undefined;

  const normalizedName = typeName.toLowerCase().replace(/\s+/g, '');

  // Try exact matches first
  if (DEFAULT_IMAGES[normalizedName]) {
    return DEFAULT_IMAGES[normalizedName];
  }

  // Check for substring matches in specific order (check longer patterns first)
  if (normalizedName.includes('registertryitteam')) {
    return DEFAULT_IMAGES.registertryitteam;
  }

  if (normalizedName.includes('registertryitcoach')) {
    return DEFAULT_IMAGES.registertryitcoach;
  }

  if (normalizedName.includes('registertryitewd')) {
    return DEFAULT_IMAGES.registertryitewd;
  }

  if (normalizedName.includes('registertryitrider')) {
    return DEFAULT_IMAGES.registertryitrider;
  }

  if (normalizedName.includes('registertryitalumni')) {
    return DEFAULT_IMAGES.registertryitalumni;
  }

  if (normalizedName.includes('homebackground')) {
    return DEFAULT_IMAGES.homebackground;
  }

  // Check for RegisterRiderEWD before RegisterRider (more specific first)
  if (
    normalizedName.includes('registerewd') ||
    normalizedName.includes('riderewd')
  ) {
    return DEFAULT_IMAGES.registerewd;
  }

  if (normalizedName.includes('registerteam')) {
    return DEFAULT_IMAGES.registerteam;
  }

  if (normalizedName.includes('registercoach')) {
    return DEFAULT_IMAGES.registercoach;
  }

  if (normalizedName.includes('registerrider')) {
    return DEFAULT_IMAGES.registerrider;
  }

  // Check specific keywords (more specific first)
  if (normalizedName.includes('alumni')) {
    return DEFAULT_IMAGES.alumni;
  }

  if (normalizedName.includes('ewd')) {
    return DEFAULT_IMAGES.ewd;
  }

  if (normalizedName.includes('rider')) {
    return DEFAULT_IMAGES.rider;
  }
  if (normalizedName.includes('associate')) {
    return DEFAULT_IMAGES.associate;
  }
  if (normalizedName.includes('riderdashboardbgimage')) {
    return DEFAULT_IMAGES.RiderDashboardBgImage;
  }

  return undefined;
};

const ImageCardComponent: React.FC<ImageCardComponentProps> = ({
  image,
  onUpload,
  onDelete,
  isLoading = false,
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isCardLoading, setIsCardLoading] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [isDragActive, setIsDragActive] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{ file?: string }>(
    {},
  );
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [imageInfo, setImageInfo] = useState<{ width: number; height: number }>(
    {
      width: 0,
      height: 0,
    },
  );

  const formatTypeName = (name: string): string => {
    // Insert space before capital letters that follow lowercase letters
    const spaced = name.replace(/([a-z])([A-Z])/g, '$1 $2').trim();
    // Capitalize each word
    return spaced.replace(/\b\w/g, char => char.toUpperCase());
  };

  const ALLOWED_IMAGE_TYPES = [
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/webp',
  ];

  const processFile = (file: File) => {
    setSelectedFile(file);

    // Generate preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        alert(
          'Invalid file format. Only PNG, JPG, JPEG, and WEBP files are allowed.',
        );
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      processFile(file);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];

      // Validate file type
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        alert(
          'Invalid file format. Only PNG, JPG, JPEG, and WEBP files are allowed.',
        );
        return;
      }

      processFile(file);
    }
  };

  const handleCropComplete = (croppedBlob: Blob, croppedImageUrl: string) => {
    const croppedFile = new File(
      [croppedBlob],
      selectedFile?.name || 'cropped-image.png',
      { type: 'image/png' },
    );
    setSelectedFile(croppedFile);
    setImagePreview(croppedImageUrl);
    setShowCropper(false);

    // Clear file error when user crops image
    if (validationErrors.file) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.file;
        return newErrors;
      });
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setSelectedFile(null);
    setImagePreview('');
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
    setImagePreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSaveImage = async () => {
    if (!selectedFile) {
      setValidationErrors({ file: 'Image is required' });
      return;
    }

    setIsCardLoading(true);
    try {
      if (image.type !== undefined) {
        await onUpload(image.type, selectedFile);
        resetModal();
      }
    } finally {
      setIsCardLoading(false);
    }
  };

  const resetModal = () => {
    setSelectedFile(null);
    setImagePreview('');
    setShowCropper(false);
    setValidationErrors({});
    setShowUploadModal(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteClick = () => {
    setShowDeletePopup(true);
  };

  const handleDeleteConfirm = async () => {
    if (image.type !== undefined) {
      setIsCardLoading(true);
      try {
        await onDelete(image.type);
        setShowDeletePopup(false);
      } finally {
        setIsCardLoading(false);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeletePopup(false);
  };
  const handlePreviewClick = async () => {
    if (displayImageUrl) {
      const img = new Image();
      img.onload = async () => {
        try {
          setImageInfo({
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
        } catch (error) {
          setImageInfo({
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
        }
      };
      img.src = displayImageUrl;
    }
    setShowFullPreview(true);
  };

  // Use imageUrl if available, otherwise use default image for the type
  const displayImageUrl = image.imageUrl
    ? image.imageUrl.replace(/\\/g, '/')
    : getDefaultImageUrl(image.typeName);

  return (
    <>
      <ImageCard>
        <ImagePreview imageUrl={displayImageUrl}>
          {displayImageUrl ? (
            <PreviewButton onClick={handlePreviewClick} title="View full image">
              Preview
            </PreviewButton>
          ) : (
            <NoPreviewPlaceholder>
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="3"
                  y="3"
                  width="18"
                  height="18"
                  rx="2"
                  stroke="#9ca3af"
                  strokeWidth="1.5"
                />
                <line
                  x1="3"
                  y1="3"
                  x2="21"
                  y2="21"
                  stroke="#9ca3af"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <circle cx="8.5" cy="8.5" r="1.5" fill="#9ca3af" />
                <path
                  d="M5 17L9 13L13 17"
                  stroke="#9ca3af"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>No Preview Available</span>
            </NoPreviewPlaceholder>
          )}
        </ImagePreview>
        <CardContent>
          <CardTitle>
            {formatTypeName(image.typeName || `Type ${image.type}`)}
          </CardTitle>
          <CardActions>
            <Button
              onClick={() => setShowUploadModal(true)}
              disabled={isCardLoading || isLoading}
            >
              {isCardLoading ? 'Uploading...' : 'Upload'}
            </Button>
            {image.hasImage && (
              <Button
                variant="danger"
                onClick={handleDeleteClick}
                disabled={isCardLoading || isLoading}
              >
                Delete
              </Button>
            )}
          </CardActions>
        </CardContent>
        <HiddenFileInput
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp"
          onChange={handleFileInputChange}
        />
      </ImageCard>
      {showFullPreview && displayImageUrl && (
        <FullImagePreviewModal onClick={() => setShowFullPreview(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <CloseButton onClick={() => setShowFullPreview(false)}>
              ✕
            </CloseButton>
            <ModalImage src={displayImageUrl} alt={image.typeName || ''} />

            <ImageInfoPanel>
              <ImageInfo>
                <span className="label">Image Type:</span>
                <span>
                  {formatTypeName(image.typeName || `Type ${image.type}`)}
                </span>
              </ImageInfo>
              <ImageInfo>
                <span className="label">Dimensions:</span>
                <span>
                  {imageInfo.width} × {imageInfo.height} px
                </span>
              </ImageInfo>
            </ImageInfoPanel>
          </ModalContent>
        </FullImagePreviewModal>
      )}

      {/* Upload Modal */}
      <AdminCustomPopUp
        className="add-registration-image-popup-style"
        visible={showUploadModal}
        setVisible={setShowUploadModal}
        closeToggle={resetModal}
        defaultPopupFooter={false}
        align="centre"
        popupLabel={`Upload ${formatTypeName(image.typeName || 'Image')}`}
        children={
          <>
            {isCardLoading ? (
              <CommonLoader loaderType="login" />
            ) : (
              <FlexWrapper>
                {/* Image Upload Area */}
                {!imagePreview && (
                  <div>
                    <ImageUploadArea
                      isDragActive={isDragActive}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <UploadIcon>
                        <svg
                          width="48"
                          height="48"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect
                            x="2"
                            y="2"
                            width="20"
                            height="20"
                            rx="2"
                            stroke="#3b82f6"
                            strokeWidth="1.5"
                          />
                          <circle cx="6.5" cy="6.5" r="1.5" fill="#3b82f6" />
                          <path
                            d="M2 16L7 11L12 16L22 6V22C22 23.1 21.1 24 20 24H4C2.9 24 2 23.1 2 22V16Z"
                            fill="#3b82f6"
                            fillOpacity="0.3"
                          />
                          <path
                            d="M2 16L7 11L12 16L22 6"
                            stroke="#3b82f6"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </UploadIcon>
                      <UploadText>Click to upload or drag and drop</UploadText>
                      <UploadSubText>PNG, JPG, WEBP up to 10MB</UploadSubText>
                    </ImageUploadArea>
                    <HiddenFileInput
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      onChange={handleFileInputChange}
                    />
                    {validationErrors.file && (
                      <Errortext>{validationErrors.file}</Errortext>
                    )}
                  </div>
                )}

                {/* Image Cropper */}
                {showCropper && imagePreview && (
                  <AdminCustomImageCropper
                    imageUrl={imagePreview}
                    onCropComplete={handleCropComplete}
                    onCancel={handleCropCancel}
                    defaultAspectRatio={16 / 9}
                    minWidth={50}
                    minHeight={50}
                    showAspectRatioControls={true}
                    cropButtonLabel="Crop & Continue"
                    cancelButtonLabel="Cancel"
                  />
                )}

                {/* Image Preview */}
                {!showCropper && imagePreview && (
                  <>
                    <div>
                      <ImagePreviewContainer>
                        <PreviewImage src={imagePreview} alt="Preview" />
                        <RemoveImageButton onClick={handleRemoveImage}>
                          ✕
                        </RemoveImageButton>
                      </ImagePreviewContainer>
                    </div>

                    {/* Action Buttons */}
                    <ButtonWrapper>
                      <AdminCustomButton
                        buttonType="cancel"
                        onClick={resetModal}
                      >
                        Cancel
                      </AdminCustomButton>
                      <AdminCustomButton
                        buttonType="primary"
                        isActive={!!selectedFile}
                        className="button-style"
                        onClick={handleSaveImage}
                      >
                        Save
                      </AdminCustomButton>
                    </ButtonWrapper>
                  </>
                )}
              </FlexWrapper>
            )}
          </>
        }
      />

      {/* Delete Confirmation Modal */}
      <CustomDeletePopup
        deleteIcon={true}
        headerUnderline={false}
        popupSubLabel="Are you sure you want to delete this registration page image?"
        className="body-scroll-lock height-full delete-update"
        popupLabel="Delete Image"
        setVisible={handleDeleteCancel}
        visible={showDeletePopup}
        align="centre"
        btnLabel1="Cancel"
        btnLabel2="Delete"
        handlebtn2Click={handleDeleteConfirm}
        handlebtn1Click={handleDeleteCancel}
      />
    </>
  );
};

const ImageDashboard: React.FC = () => {
  const {
    loading,
    isUploading,
    isDeleting,
    allImages,
    getAllRegistrationPageImages,
    uploadOrUpdateRegistrationPageImage,
    deleteRegistrationPageImage,
  } = useRegistrationPageImageApi();
  useEffect(() => {
    getAllRegistrationPageImages();
  }, []);

  if (loading) {
    return (
      <DashboardContainer>
        <GridContainer>
          <ImageCardSkeletonLoader count={allImages?.length || 12} />
        </GridContainer>
      </DashboardContainer>
    );
  }

  if (!allImages || allImages.length === 0) {
    return (
      <DashboardContainer>
        <GridContainer>
          <NoDataContainer>No background images available</NoDataContainer>
        </GridContainer>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <GridContainer>
        {allImages.map((image, index) => (
          <ImageCardComponent
            key={index}
            image={image}
            onUpload={uploadOrUpdateRegistrationPageImage}
            onDelete={deleteRegistrationPageImage}
            isLoading={isUploading || isDeleting || loading}
          />
        ))}
      </GridContainer>
    </DashboardContainer>
  );
};

export default ImageDashboard;
